import { useMemo, useState, type ReactNode } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import toast from 'react-hot-toast';
import type { Vacancy } from '@/api/generated/types.gen';
import { GreetLogo } from '@/components/brand/GreetLogo';
import { GreetWidget, type WidgetState } from '@/components/GreetWidget/GreetWidget';
import {
  BRAND_GREEN,
  WIDGET_SIZES,
  WIDGET_THEMES,
  WIDGET_WIDTH,
  buildEmbedSnippet,
  resolveAccent,
  type WidgetMode,
  type WidgetPosition,
  type WidgetSize,
  type WidgetTheme,
} from '@/components/GreetWidget/widgetConfig';
import { WIDGET_SAMPLES, type WidgetSampleKey } from '@/components/GreetWidget/widgetSamples';
import { FloatingPreview } from '@/components/GreetWidget/studio/FloatingPreview';
import { MOCK_SITES, MockSite, type MockSiteKey } from '@/components/GreetWidget/studio/MockSites';

// Design studio for the embeddable voice form: every option, size, theme and state,
// placed on realistic host sites. No backend; Start animates to the started state.
export const Route = createFileRoute('/dev/widget-preview')({
  component: WidgetStudio,
  head: () => ({ meta: [{ title: 'Widget studio · greet' }] }),
});

type View = 'site' | 'grid';
type Device = 'desktop' | 'mobile';
type StateChoice = 'live' | WidgetState;

const SITE_COMPANY: Record<MockSiteKey, WidgetSampleKey> = {
  telehealth: 'telehealth',
  careers: 'careers',
  saas: 'saas',
};

const SITE_DEFAULTS: Record<MockSiteKey, { size: WidgetSize; theme: WidgetTheme }> = {
  telehealth: { size: 'standard', theme: 'light' },
  careers: { size: 'compact', theme: 'light' },
  saas: { size: 'large', theme: 'dark' },
};

const ACCENTS: { label: string; value: string | null }[] = [
  { label: 'Company colour', value: null },
  { label: 'greet green', value: BRAND_GREEN },
  { label: 'Teal', value: '#0F766E' },
  { label: 'Blue', value: '#2563EB' },
  { label: 'Ink', value: '#111111' },
  { label: 'Rose', value: '#E11D48' },
];

const STATES: { value: StateChoice; label: string }[] = [
  { value: 'live', label: 'Live' },
  { value: 'errors', label: 'Errors' },
  { value: 'starting', label: 'Starting' },
  { value: 'started', label: 'Started' },
  { value: 'blocked', label: 'Popup blocked' },
  { value: 'unavailable', label: 'Closed' },
];

const COMPANY_LABELS: Record<WidgetSampleKey, string> = {
  telehealth: 'Northwind Telehealth',
  careers: 'Lumen Dental',
  saas: 'Harbor Analytics',
  'long-name': 'Long names',
};

function Segmented<T extends string>({
  value,
  options,
  onChange,
}: {
  value: T;
  options: { value: NoInfer<T>; label: string }[];
  onChange: (v: NoInfer<T>) => void;
}) {
  return (
    <div className="flex flex-wrap gap-1 rounded-[10px] bg-neutral-100 p-1">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          aria-pressed={value === o.value}
          className={`flex-1 cursor-pointer rounded-[7px] px-2.5 py-1.5 text-[12.5px] font-medium whitespace-nowrap transition-colors ${
            value === o.value ? 'bg-white text-neutral-900 shadow-sm' : 'text-neutral-500 hover:text-neutral-800'
          }`}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <p className="mb-1.5 text-[11.5px] font-medium tracking-wide text-neutral-400 uppercase">{label}</p>
      {children}
    </div>
  );
}

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

function WidgetStudio() {
  const [view, setView] = useState<View>('site');
  const [site, setSite] = useState<MockSiteKey>('telehealth');
  const [mode, setMode] = useState<WidgetMode>('inline');
  const [size, setSize] = useState<WidgetSize>('standard');
  const [theme, setTheme] = useState<WidgetTheme>('light');
  const [accent, setAccent] = useState<string | null>(null);
  const [video, setVideo] = useState(false);
  const [company, setCompany] = useState<WidgetSampleKey>('telehealth');
  const [stateChoice, setStateChoice] = useState<StateChoice>('live');
  const [device, setDevice] = useState<Device>('desktop');
  const [position, setPosition] = useState<WidgetPosition>('bottom-right');
  // Remount the widget when a control changes so a "started" card resets.
  const [nonce, setNonce] = useState(0);

  const vacancy = useMemo<Vacancy>(() => {
    const base = WIDGET_SAMPLES[company];
    return { ...base, mind_config: { ...base.mind_config, camera_required: video } };
  }, [company, video]);

  const pickSite = (key: MockSiteKey) => {
    setSite(key);
    setCompany(SITE_COMPANY[key]);
    setSize(SITE_DEFAULTS[key].size);
    setTheme(SITE_DEFAULTS[key].theme);
    setNonce((n) => n + 1);
  };

  const forceState = stateChoice === 'live' ? undefined : stateChoice;
  const resolvedAccent = resolveAccent(accent, vacancy.company_info?.apply_page_theme);
  const snippet = buildEmbedSnippet({
    publicId: 'YOUR_SESSION_ID',
    mode,
    size,
    theme,
    accent,
    position,
    label: mode === 'floating' ? 'Talk to us' : undefined,
  });

  const widget = (onClose?: () => void) => (
    <GreetWidget
      key={`${nonce}-${company}-${video}`}
      vacancy={stateChoice === 'unavailable' ? undefined : vacancy}
      size={size}
      theme={theme}
      accent={accent}
      mode={mode}
      preview
      forceState={forceState}
      onClose={onClose}
    />
  );

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(snippet);
      toast.success('Copied');
    } catch {
      toast.error('Copy failed');
    }
  };

  const narrow = device === 'mobile';

  return (
    <div className="flex min-h-dvh flex-col bg-neutral-50 text-neutral-900 lg:flex-row">
      {/* Controls */}
      <aside className="shrink-0 border-b border-neutral-200 bg-white lg:sticky lg:top-0 lg:h-dvh lg:w-[300px] lg:overflow-y-auto lg:border-r lg:border-b-0">
        <div className="flex items-center gap-2 px-5 pt-5 pb-4">
          <GreetLogo className="h-5 w-auto" handColor={BRAND_GREEN} />
          <span className="text-[13px] font-medium text-neutral-400">Widget studio</span>
        </div>
        <div className="grid gap-5 px-5 pb-6 sm:grid-cols-2 lg:grid-cols-1">
          <Field label="View">
            <Segmented
              value={view}
              onChange={setView}
              options={[
                { value: 'site', label: 'On a website' },
                { value: 'grid', label: 'All variants' },
              ]}
            />
          </Field>
          {view === 'site' && (
            <Field label="Website">
              <Segmented
                value={site}
                onChange={pickSite}
                options={(Object.keys(MOCK_SITES) as MockSiteKey[]).map((k) => ({
                  value: k,
                  label: MOCK_SITES[k].label.split(' ')[0],
                }))}
              />
            </Field>
          )}
          {view === 'site' && (
            <Field label="Placement">
              <Segmented
                value={mode}
                onChange={(v) => {
                  setMode(v);
                  setNonce((n) => n + 1);
                }}
                options={[
                  { value: 'inline', label: 'Inline' },
                  { value: 'floating', label: 'Floating button' },
                ]}
              />
            </Field>
          )}
          {view === 'site' && mode === 'floating' && (
            <Field label="Position">
              <Segmented
                value={position}
                onChange={setPosition}
                options={[
                  { value: 'bottom-left', label: 'Bottom left' },
                  { value: 'bottom-right', label: 'Bottom right' },
                ]}
              />
            </Field>
          )}
          {view === 'site' && (
            <Field label="Size">
              <Segmented value={size} onChange={setSize} options={WIDGET_SIZES.map((v) => ({ value: v, label: cap(v) }))} />
            </Field>
          )}
          <Field label="Theme">
            <Segmented value={theme} onChange={setTheme} options={WIDGET_THEMES.map((v) => ({ value: v, label: cap(v) }))} />
          </Field>
          <Field label="Accent">
            <div className="flex flex-wrap gap-2">
              {ACCENTS.map((a) => {
                const color = a.value ?? resolveAccent(null, vacancy.company_info?.apply_page_theme);
                const on = accent === a.value;
                return (
                  <button
                    key={a.label}
                    type="button"
                    title={a.label}
                    aria-label={a.label}
                    aria-pressed={on}
                    onClick={() => setAccent(a.value)}
                    className={`relative size-8 cursor-pointer rounded-full ring-offset-2 transition-shadow ${
                      on ? 'ring-2 ring-neutral-900' : 'ring-1 ring-black/10 hover:ring-black/30'
                    }`}
                    style={{ background: color }}>
                    {a.value === null && (
                      <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold text-black/60">
                        A
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </Field>
          <Field label="Answers by">
            <Segmented
              value={video ? 'video' : 'voice'}
              onChange={(v) => setVideo(v === 'video')}
              options={[
                { value: 'voice', label: 'Voice' },
                { value: 'video', label: 'Video' },
              ]}
            />
          </Field>
          <Field label="Company">
            <Segmented
              value={company}
              onChange={(v) => {
                setCompany(v);
                setNonce((n) => n + 1);
              }}
              options={(Object.keys(WIDGET_SAMPLES) as WidgetSampleKey[]).map((k) => ({
                value: k,
                label: COMPANY_LABELS[k].split(' ')[0],
              }))}
            />
          </Field>
          <Field label="State">
            <Segmented value={stateChoice} onChange={setStateChoice} options={STATES} />
          </Field>
          {view === 'site' && (
            <Field label="Device">
              <Segmented
                value={device}
                onChange={setDevice}
                options={[
                  { value: 'desktop', label: 'Desktop' },
                  { value: 'mobile', label: 'Phone' },
                ]}
              />
            </Field>
          )}
        </div>
      </aside>

      {/* Canvas */}
      <main className="min-w-0 flex-1 p-4 sm:p-6 lg:p-8">
        {view === 'site' ? (
          <div className="flex flex-col items-center gap-6">
            <DeviceFrame device={device} url={MOCK_SITES[site].url}>
              <MockSite
                site={site}
                widget={
                  mode === 'inline' ? (
                    <div className="w-full" style={{ maxWidth: WIDGET_WIDTH[size] }}>
                      {widget()}
                    </div>
                  ) : null
                }
              />
              {mode === 'floating' && (
                <FloatingPreview
                  key={`${nonce}-${device}`}
                  accent={resolvedAccent}
                  size={size}
                  position={position}
                  label="Talk to us"
                  narrow={narrow}>
                  {(close) => widget(close)}
                </FloatingPreview>
              )}
            </DeviceFrame>
            <EmbedCode snippet={snippet} onCopy={copy} />
          </div>
        ) : (
          <VariantGrid vacancy={vacancy} accent={accent} theme={theme} forceState={forceState} nonce={nonce} />
        )}
      </main>
    </div>
  );
}

function DeviceFrame({ device, url, children }: { device: Device; url: string; children: ReactNode }) {
  if (device === 'mobile') {
    return (
      <div className="rounded-[46px] bg-neutral-900 p-3 shadow-2xl">
        <div className="relative h-[760px] w-[375px] overflow-hidden rounded-[36px] bg-white">
          <div className="flex h-11 items-center justify-center bg-white/90 text-[11px] font-medium text-neutral-500 backdrop-blur">
            {url}
          </div>
          <div className="@container relative h-[calc(100%-44px)] overflow-y-auto">{children}</div>
        </div>
      </div>
    );
  }
  return (
    <div className="w-full max-w-[1180px] overflow-hidden rounded-xl bg-white shadow-[0_30px_80px_-30px_rgba(0,0,0,.35)] ring-1 ring-black/10">
      <div className="flex h-10 items-center gap-2 border-b border-neutral-200 bg-neutral-100 px-4">
        <span className="size-3 rounded-full bg-[#FF5F57]" />
        <span className="size-3 rounded-full bg-[#FEBC2E]" />
        <span className="size-3 rounded-full bg-[#28C840]" />
        <span className="mx-auto rounded-md bg-white px-16 py-1 text-[12px] text-neutral-500 ring-1 ring-black/5">{url}</span>
      </div>
      <div className="@container relative h-[720px] overflow-y-auto">{children}</div>
    </div>
  );
}

function EmbedCode({ snippet, onCopy }: { snippet: string; onCopy: () => void }) {
  return (
    <section className="w-full max-w-[1180px] overflow-hidden rounded-xl bg-neutral-950 text-neutral-100 ring-1 ring-black/10">
      <div className="flex items-center justify-between px-4 py-3">
        <span className="text-[13px] font-medium text-neutral-400">Embed code · paste before &lt;/body&gt;</span>
        <button
          type="button"
          onClick={onCopy}
          className="cursor-pointer rounded-md bg-white/10 px-3 py-1.5 text-[12.5px] font-medium hover:bg-white/15">
          Copy
        </button>
      </div>
      <pre className="overflow-x-auto px-4 pb-4 font-mono text-[12.5px] leading-relaxed text-neutral-300">
        <code>{snippet}</code>
      </pre>
    </section>
  );
}

const THEME_BG: Record<WidgetTheme, string> = {
  light: 'bg-[#EEF0F2]',
  dark: 'bg-[#16171A]',
  auto: 'bg-[linear-gradient(135deg,#EEF0F2_50%,#16171A_50%)]',
};

function VariantGrid({
  vacancy,
  accent,
  theme,
  forceState,
  nonce,
}: {
  vacancy: Vacancy;
  accent: string | null;
  theme: WidgetTheme;
  forceState?: WidgetState;
  nonce: number;
}) {
  const allStates: WidgetState[] = ['idle', 'errors', 'starting', 'started', 'blocked', 'unavailable'];
  return (
    <div className="space-y-10">
      {WIDGET_THEMES.map((t) => (
        <section key={t}>
          <h2 className="mb-3 text-[13px] font-medium text-neutral-500">{cap(t)} theme</h2>
          <div className={`flex flex-wrap items-start gap-6 rounded-2xl p-6 ${THEME_BG[t]}`}>
            {WIDGET_SIZES.map((s) => (
              <figure key={s} style={{ width: WIDGET_WIDTH[s] }} className="max-w-full">
                <GreetWidget
                  key={`${nonce}-${s}-${t}`}
                  vacancy={forceState === 'unavailable' ? undefined : vacancy}
                  size={s}
                  theme={t}
                  accent={accent}
                  preview
                  forceState={forceState}
                />
                <figcaption className={`mt-2 text-[12px] ${t === 'dark' ? 'text-white/50' : 'text-neutral-500'}`}>
                  {cap(s)} · {WIDGET_WIDTH[s]}px
                </figcaption>
              </figure>
            ))}
          </div>
        </section>
      ))}
      <section>
        <h2 className="mb-3 text-[13px] font-medium text-neutral-500">Every state · standard · {theme}</h2>
        <div className={`flex flex-wrap items-start gap-6 rounded-2xl p-6 ${THEME_BG[theme]}`}>
          {allStates.map((st) => (
            <figure key={st} style={{ width: WIDGET_WIDTH.standard }} className="max-w-full">
              <GreetWidget
                vacancy={st === 'unavailable' ? undefined : vacancy}
                size="standard"
                theme={theme}
                accent={accent}
                preview
                forceState={st}
              />
              <figcaption className={`mt-2 text-[12px] ${theme === 'dark' ? 'text-white/50' : 'text-neutral-500'}`}>
                {cap(st)}
              </figcaption>
            </figure>
          ))}
        </div>
      </section>
    </div>
  );
}
